import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bot, ChartNoAxesColumn } from 'lucide-react';
import { errorService } from '../../services/errorService';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import ReactFlow, {
  Background,
  EdgeChange,
  NodeChange,
  ReactFlowInstance,
  ReactFlowProvider,
  type OnSelectionChangeFunc
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useSimulationStore, useGraph, useIsRunning, useLastResult, useSelectedNodeId, useRunConfig } from '../../store/simulationStore';
import { useVersionStore, type SavedVersion, type Simulation } from '../../store/versionStore';
import { useScenarioStore } from '../../store/scenarioStore';
import { supabase } from '../../lib/supabaseClient';
import { NodePalette } from './components/NodePalette';
import { AddInputMenu } from './components/AddInputMenu';
import { SimulationNodeCard } from './components/SimulationNodeCard';
import { AssistantPanel } from './components/AssistantPanel';
import { InputsAssumptionsTable } from './components/InputsAssumptionsTable';
import { NodeOptionsPanel } from './components/NodeOptionsPanel';
import { ScenariosPanel } from './components/ScenariosPanel';
import { useSimulationRunner } from '../simulation/useSimulationRunner';
import { collectSimulationRunErrors } from '../simulation/validation';
import type { DistributionConfig, SimulationGraph } from '../../models/simulation';
import { NodeSearch } from './components/NodeSearch';
import { useThemeStore } from '../../store/themeStore';
import { HistoryControls } from './components/HistoryControls';
import { VersionHistoryPanel } from './components/VersionHistoryPanel';
import { ResultsView } from './components/ResultsView';
import { ModelSheetView } from './components/ModelSheetView';
import { useAutoSave } from '../../hooks/useAutoSave';
import {
  DEFAULT_WORKSPACE_VIEW,
  isWorkspaceView,
  resolveWorkspaceView,
  useViewStore,
  type WorkspaceView
} from '../../store/viewStore';
import { WORKSPACE_FIT_VIEW_PADDING, WORKSPACE_MAX_ZOOM, WORKSPACE_MIN_ZOOM } from './zoomLimits';
import { buildGraphViewModel } from './graphViewModel';
import { layoutGraphWithDefault } from '../../utils/layout';
import { productMessaging } from '../../config/messaging';

const WORKSPACE_NARROW_WIDTH = 900;

const applyAutomaticGraphLayout = async (graph: SimulationGraph): Promise<SimulationGraph> => {
  const positions = await layoutGraphWithDefault(graph);

  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const position = positions[node.id];

      if (!position) {
        return node;
      }

      return {
        ...node,
        position: { x: position.x, y: position.y }
      };
    })
  };
};

const createTransientWorkspaceGraph = (name = 'Loading simulation'): SimulationGraph => {
  const now = new Date().toISOString();

  return {
    id: `transient-${Date.now()}`,
    name,
    version: 1,
    nodes: [],
    edges: [],
    createdAt: now,
    updatedAt: now,
    dataUpdatedAt: now,
    customUnits: []
  };
};

const fetchSimulationById = async (simulationId: string): Promise<Simulation | undefined> => {
  const { data, error } = await supabase
    .from('simulations')
    .select('*')
    .eq('id', simulationId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ? (data as Simulation) : undefined;
};

const defaultDistribution = (name: string): DistributionConfig => ({
  kind: 'normal',
  mean: 100,
  std: 10,
  min: 0,
  max: 200,
  label: name
});

const applyWorkspaceViewParam = (params: URLSearchParams, view: WorkspaceView) => {
  if (view === DEFAULT_WORKSPACE_VIEW) {
    params.delete('view');
    return;
  }

  params.set('view', view);
};

interface SimulationCanvasProps {
  setReactFlowInstance: (instance: ReactFlowInstance) => void;
  onOpenHistory: () => void;
  historyDisabled: boolean;
  onOpenNodeOptions: (nodeId: string) => void;
  isReadOnly?: boolean;
  isGraphLoading?: boolean;
}

const openAssistantWithPrompt = (prompt?: string) => {
  window.dispatchEvent(new CustomEvent('simcarlo:open-assistant', { detail: { prompt } }));
};

const SimulationCanvas = ({
  setReactFlowInstance: setReactFlowInstanceProp,
  onOpenHistory,
  historyDisabled,
  onOpenNodeOptions,
  isReadOnly = false,
  isGraphLoading = false
}: SimulationCanvasProps) => {
  const graph = useGraph();
  const result = useLastResult();
  const selectedNodeId = useSelectedNodeId();
  const addNode = useSimulationStore((state) => state.addNode);
  const updateNode = useSimulationStore((state) => state.updateNode);
  const setSelection = useSimulationStore((state) => state.setSelection);
  const tidyLayout = useSimulationStore((state) => state.tidyLayout);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const theme = useThemeStore((state) => state.theme);
  const nodeTypes = useMemo(() => ({ simulation: SimulationNodeCard }), []);
  const fittedGraphIdRef = useRef<string | null>(null);
  const graphStructureRef = useRef<{
    graphId: string;
    nodeCount: number;
    edgePairs: Set<string>;
  }>({
    graphId: graph.id,
    nodeCount: graph.nodes.length,
    edgePairs: new Set(graph.edges.map((edge) => `${edge.source}->${edge.target}`))
  });

  const { nodes, edges } = useMemo(
    () => buildGraphViewModel({
      graph,
      selectedNodeId,
      hoveredOutputNodeId: null,
      result,
      onOpenOptions: onOpenNodeOptions
    }),
    [graph, selectedNodeId, result, onOpenNodeOptions]
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      changes.forEach((change) => {
        if (change.type === 'position') {
          const { id } = change;
          const nextPosition = change.position ?? change.positionAbsolute;
          if (nextPosition) {
            // Directly update position without triggering data changes
            updateNode(id, (node) => {
              node.position = { x: nextPosition.x, y: nextPosition.y };
            });
          }
        }
        if (change.type === 'remove') {
          setSelection(undefined);
        }
        if (change.type === 'select') {
          setSelection(change.selected ? change.id : undefined);
        }
      });
    },
    [setSelection, updateNode]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Edges are auto-generated, so we don't allow manual deletion
      // Filter out remove changes to make edges read-only
      changes.forEach((change) => {
        if (change.type === 'remove') {
          // Ignore - edges are auto-generated
        }
      });
    },
    []
  );

  const onSelectionChange = useCallback<OnSelectionChangeFunc>(
    ({ nodes: selectedNodes }) => {
      // Only update if we have nodes selected, or if explicitly clicking outside
      // This prevents deselection when typing in inspector panel
      if (selectedNodes.length > 0) {
        const node = selectedNodes[0];
        setSelection(node?.id);
      }
    },
    [setSelection]
  );

  const onInit = useCallback((instance: ReactFlowInstance) => {
    setReactFlowInstance(instance);
    setReactFlowInstanceProp(instance);
    instance.fitView({
      padding: WORKSPACE_FIT_VIEW_PADDING,
      minZoom: WORKSPACE_MIN_ZOOM,
      maxZoom: WORKSPACE_MAX_ZOOM
    });
  }, [setReactFlowInstanceProp]);

  useEffect(() => {
    if (!reactFlowInstance || graph.nodes.length === 0 || fittedGraphIdRef.current === graph.id) {
      return;
    }

    fittedGraphIdRef.current = graph.id;
    const frame = requestAnimationFrame(() => {
      reactFlowInstance.fitView({
        padding: WORKSPACE_FIT_VIEW_PADDING,
        minZoom: WORKSPACE_MIN_ZOOM,
        maxZoom: WORKSPACE_MAX_ZOOM,
        duration: 0
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [graph.id, graph.nodes.length, reactFlowInstance]);

  // Automatically tidy whenever a node is added or a new connection appears.
  useEffect(() => {
    const nextEdgePairs = new Set(graph.edges.map((edge) => `${edge.source}->${edge.target}`));
    const previousStructure = graphStructureRef.current;

    if (graph.id !== previousStructure.graphId) {
      graphStructureRef.current = {
        graphId: graph.id,
        nodeCount: graph.nodes.length,
        edgePairs: nextEdgePairs
      };
      return;
    }

    const hasNewNode = graph.nodes.length > previousStructure.nodeCount;
    const hasNewConnection = Array.from(nextEdgePairs).some((edgePair) => !previousStructure.edgePairs.has(edgePair));

    graphStructureRef.current = {
      graphId: graph.id,
      nodeCount: graph.nodes.length,
      edgePairs: nextEdgePairs
    };

    if (!isReadOnly && (hasNewNode || hasNewConnection)) {
      void tidyLayout();
    }
  }, [graph.id, graph.nodes.length, graph.edges, isReadOnly, tidyLayout]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      // Prevent dropping nodes if in read-only mode
      if (isReadOnly) {
        return;
      }

      const type = event.dataTransfer.getData('application/reactflow');
      
      // Check if reactFlowInstance is available
      if (!reactFlowInstance || !type) {
        return;
      }

      // Get the position where the node was dropped
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Create the appropriate node based on type
      switch (type) {
        case 'distribution':
          addNode(
            {
              type: 'distribution',
              name: 'New Distribution',
              description: 'Random input variable',
              config: defaultDistribution('New Distribution')
            },
            position
          );
          break;
        case 'formula':
          addNode(
            {
              type: 'formula',
              name: 'New Formula',
              description: 'Expression referencing inputs',
              expression: ''
            },
            position
          );
          break;
      }
    },
    [reactFlowInstance, addNode, isReadOnly]
  );

  const handleAddStarterDistribution = useCallback(() => {
    addNode(
      {
        type: 'distribution',
        name: 'Input variable',
        description: 'Uncertain model driver',
        config: defaultDistribution('Input variable')
      },
      { x: 120, y: 120 }
    );
  }, [addNode]);

  const handleAddStarterFormula = useCallback(() => {
    addNode(
      {
        type: 'formula',
        name: 'Profit output',
        description: 'Formula for the decision KPI',
        expression: '',
        isOutput: true
      },
      { x: 420, y: 120 }
    );
  }, [addNode]);

  return (
    <ReactFlow
      key={graph.id}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onPaneClick={() => setSelection(undefined)}
      onSelectionChange={onSelectionChange}
      onInit={onInit}
      onDrop={onDrop}
      onDragOver={onDragOver}
      minZoom={WORKSPACE_MIN_ZOOM}
      maxZoom={WORKSPACE_MAX_ZOOM}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={{
        type: 'smoothstep'
      }}
      edgesUpdatable={false}
      edgesFocusable={false}
      connectOnClick={false}
      // Trackpad gesture support
      panOnScroll={true}
      zoomOnScroll={false}
      zoomOnPinch={true}
      zoomOnDoubleClick={false}
      panOnDrag={[1, 2]}
      selectionOnDrag={false}
      onlyRenderVisibleElements={false}
    >
      <NodeSearch reactFlowInstance={reactFlowInstance} />
      {isGraphLoading && (
        <div
          className="workspace-graph-loading-overlay nodrag nowheel"
          role="status"
          aria-label="Graph loading"
          aria-live="polite"
        >
          <div className="workspace-graph-loading-card">
            <div className="workspace-graph-loading-visual" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <div>
              <p className="workspace-graph-loading-title">Loading graph</p>
              <p className="workspace-graph-loading-copy">Large models can take a while. Please wait.</p>
            </div>
          </div>
        </div>
      )}
      {!isGraphLoading && !isReadOnly && graph.nodes.length === 0 && (
        <div className="workspace-empty-canvas nodrag nowheel" role="region" aria-label="Empty graph onboarding">
          <span className="workspace-empty-kicker">New model</span>
          <h2>{productMessaging.emptyStates.graphTitle}</h2>
          <p>{productMessaging.emptyStates.graphBody}</p>
          <div className="workspace-empty-actions">
            <AddInputMenu
              className="workspace-empty-add-menu"
              buttonClassName="btn btn-primary"
              onAddDistribution={handleAddStarterDistribution}
              onAddFormula={handleAddStarterFormula}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => openAssistantWithPrompt(productMessaging.assistantPrompts.emptyGraph[0])}
            >
              <Bot size={16} aria-hidden="true" />
              Ask assistant
            </button>
            <a className="btn btn-ghost" href="/simulations">
              <ChartNoAxesColumn size={16} aria-hidden="true" />
              Try example
            </a>
          </div>
        </div>
      )}
      <div className="workspace-top-controls">
        <div className="workspace-top-controls-left">
          <HistoryControls />
        </div>
        <div className="workspace-top-utilities">
          <button
            className="version-manager-toggle"
            onClick={onOpenHistory}
            disabled={historyDisabled}
            title="Open version history"
            aria-haspopup="dialog"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon-20"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 15" />
            </svg>
            Version History
          </button>
        </div>
      </div>
      {!isReadOnly && graph.nodes.length > 0 && <NodePalette />}
      <Background gap={32} color={theme === 'dark' ? '#2a2a2a' : '#d6deeb'} />
    </ReactFlow>
  );
};


export const SimulationWorkspace = () => {
  // Enable keyboard shortcuts (Ctrl+Z for undo, Ctrl+Y for redo)
  useKeyboardShortcuts();

  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const simulationIdParam = searchParams.get('simulation');
  const versionIdParam = searchParams.get('version');
  const viewParam = searchParams.get('view');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  const [, setHoveredOutputNodeId] = useState<string | null>(null);
  const [nodeOptionsNodeId, setNodeOptionsNodeId] = useState<string | null>(null);
  const [workspaceViewportWidth, setWorkspaceViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1200 : window.innerWidth
  );
  
  // View state from store
  const activeView = useViewStore((state) => state.activeView);
  const setActiveView = useViewStore((state) => state.setActiveView);
  const isGraphView = activeView === 'graph';
  const isInputsAssumptionsView = activeView === 'inputs';
  const isModelSheetView = activeView === 'sheet';
  const isScenariosView = activeView === 'scenarios';

  useEffect(() => {
    const nextView = resolveWorkspaceView(viewParam);

    if (viewParam && !isWorkspaceView(viewParam)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('view');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    if (activeView !== nextView) {
      setActiveView(nextView);
    }
  }, [activeView, searchParams, setActiveView, setSearchParams, viewParam]);

  useEffect(() => {
    const handleResize = () => setWorkspaceViewportWidth(window.innerWidth);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const graph = useGraph();
  const runConfig = useRunConfig();
  const isRunning = useIsRunning();
  const setResult = useSimulationStore((state) => state.setResult);
  const setIsRunning = useSimulationStore((state) => state.setIsRunning);
  const setRunErrors = useSimulationStore((state) => state.setRunErrors);
  const clearRunErrors = useSimulationStore((state) => state.clearRunErrors);
  const loadGraph = useSimulationStore((state) => state.loadGraph);
  const clearHistory = useSimulationStore((state) => state.clearHistory);
  const setWorkspaceContext = useSimulationStore((state) => state.setWorkspaceContext);
  const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
  const currentSimulationName = useSimulationStore((state) => state.currentSimulationName);
  const currentVersionId = useSimulationStore((state) => state.currentVersionId);
  const setAutoSaveStatusStore = useSimulationStore((state) => state.setAutoSaveStatus);
  const setLastAutoSaveAtStore = useSimulationStore((state) => state.setLastAutoSaveAt);
  const { run } = useSimulationRunner();
  // Auto-save hook is still needed for the actual saving functionality
  useAutoSave();
  
  // Version store
  const fetchSimulations = useVersionStore((state) => state.fetchSimulations);
  const fetchVersions = useVersionStore((state) => state.fetchVersions);
  const fetchExamples = useVersionStore((state) => state.fetchExamples);
  const getVersion = useVersionStore((state) => state.getVersion);
  const getSimulation = useVersionStore((state) => state.getSimulation);
  const getLatestVersion = useVersionStore((state) => state.getLatestVersion);
  const isExample = useVersionStore((state) => state.isExample);
  const getUserAccessLevel = useVersionStore((state) => state.getUserAccessLevel);
  
  // Check if current simulation is an example (read-only)
  const isExampleSimulation = useMemo(() => {
    return currentSimulationId ? isExample(currentSimulationId) : false;
  }, [currentSimulationId, isExample]);

  // Get current access level
  const currentAccessLevel = useSimulationStore((state) => state.currentAccessLevel);
  const isViewer = currentAccessLevel === 'viewer';
  const canMutateScenarios = currentAccessLevel === 'owner' && !isExampleSimulation;
  
  // Scenario store
  const initializeForSimulation = useScenarioStore((state) => state.initializeForSimulation);
  const clearScenarios = useScenarioStore((state) => state.clearScenarios);
  const syncBaselineScenario = useScenarioStore((state) => state.syncBaselineScenario);
  const getBaselineScenario = useScenarioStore((state) => state.getBaselineScenario);
  const runScenario = useScenarioStore((state) => state.runScenario);
  
  // Track baseline sync to prevent concurrent calls
  const baselineSyncInProgressRef = useRef(false);
  const lastSyncedDataUpdatedAtRef = useRef<string | null>(null);
  const autoRunDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAutoRunKeyRef = useRef<string | null>(null);
  const workspaceLoadSeqRef = useRef(0);
  
  // Load simulation/version from URL on mount or when URL changes
  useEffect(() => {
    const loadSeq = workspaceLoadSeqRef.current + 1;
    workspaceLoadSeqRef.current = loadSeq;
    const isStaleLoad = () => workspaceLoadSeqRef.current !== loadSeq;
    const simulationId = simulationIdParam;

    if (!simulationId && !versionIdParam) {
      setIsGraphLoading(false);
      if (currentSimulationId || currentVersionId) {
        setWorkspaceContext(undefined, undefined, undefined, undefined);
        loadGraph(createTransientWorkspaceGraph('Workspace'));
        clearHistory();
      }
      return;
    }

    if (!simulationId) {
      setIsGraphLoading(false);
      setSearchParams({}, { replace: true });
      return;
    }

    if (simulationId === currentSimulationId && versionIdParam === currentVersionId) {
      setIsGraphLoading(false);
      return;
    }

    if (currentSimulationId && currentSimulationId !== simulationId) {
      setWorkspaceContext(undefined, undefined, undefined, undefined);
      loadGraph(createTransientWorkspaceGraph());
      clearHistory();
      clearScenarios();
    }

    setIsGraphLoading(true);

    const loadWorkspaceState = async () => {
      try {
        await fetchSimulations();
        if (isStaleLoad()) return;
        await fetchExamples();
        if (isStaleLoad()) return;
        await fetchVersions(simulationId);
        if (isStaleLoad()) return;

        let simulation = getSimulation(simulationId);
        if (!simulation) {
          simulation = await fetchSimulationById(simulationId);
          if (isStaleLoad()) return;
        }
        if (!simulation) {
          if (isStaleLoad()) return;
          setSearchParams({}, { replace: true });
          return;
        }

        let targetVersionId = versionIdParam ?? undefined;
        let version = targetVersionId ? getVersion(targetVersionId) : undefined;

        if (!version) {
          const latest = getLatestVersion(simulationId);
          if (latest) {
            version = latest;
            targetVersionId = latest.id;
          }
        }

        if (version) {
          if (isStaleLoad()) return;
          const clonedGraph = JSON.parse(JSON.stringify(version.graph)) as SimulationGraph;
          const graphForWorkspace = await applyAutomaticGraphLayout(clonedGraph);
          if (isStaleLoad()) return;
          loadGraph(graphForWorkspace);
          clearHistory();

          // Fetch access level
          const accessLevel = await getUserAccessLevel(simulation.id);
          if (isStaleLoad()) return;

          setWorkspaceContext(
            simulation.id,
            simulation.name,
            version.id,
            version.name,
            accessLevel || undefined
          );

          if (targetVersionId && targetVersionId !== versionIdParam) {
            const newParams = new URLSearchParams();
            newParams.set('simulation', simulation.id);
            newParams.set('version', targetVersionId);
            applyWorkspaceViewParam(newParams, activeView);
            setSearchParams(newParams, { replace: true });
          }
        } else {
          if (isStaleLoad()) return;
          // No version found - load an empty graph for new simulation
          const emptyGraph: SimulationGraph = {
            id: `temp-${Date.now()}`,
            name: simulation.name,
            version: 1,
            nodes: [],
            edges: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            dataUpdatedAt: new Date().toISOString(),
            customUnits: []
          };
          loadGraph(emptyGraph);
          clearHistory();

          // Fetch access level
          const accessLevel = await getUserAccessLevel(simulation.id);
          if (isStaleLoad()) return;

          setWorkspaceContext(simulation.id, simulation.name, undefined, undefined, accessLevel || undefined);

          if (versionIdParam) {
            const newParams = new URLSearchParams();
            newParams.set('simulation', simulation.id);
            applyWorkspaceViewParam(newParams, activeView);
            setSearchParams(newParams, { replace: true });
          }
        }
      } catch (error) {
        if (isStaleLoad()) return;
        errorService.logError('Error loading workspace state', error, {
          action: 'loadWorkspaceState',
          component: 'SimulationWorkspace',
          metadata: { simulationId, versionId: versionIdParam }
        });
        setWorkspaceContext(undefined, undefined, undefined, undefined);
        setSearchParams({}, { replace: true });
      } finally {
        if (!isStaleLoad()) {
          setIsGraphLoading(false);
        }
      }
    };

    loadWorkspaceState();
    return () => {
      if (workspaceLoadSeqRef.current === loadSeq) {
        workspaceLoadSeqRef.current += 1;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationIdParam, versionIdParam]);

  // Initialize scenarios when simulation is loaded
  useEffect(() => {
    if (currentSimulationId && graph.id) {
      initializeForSimulation(currentSimulationId, graph, { canMutate: canMutateScenarios });
    }
    
    // Reset baseline sync tracking when simulation changes
    baselineSyncInProgressRef.current = false;
    lastSyncedDataUpdatedAtRef.current = null;
    
    return () => {
      // Clear scenarios when unmounting or switching simulations
      if (!currentSimulationId) {
        clearScenarios();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSimulationId, graph.id, canMutateScenarios]);
  
  // Sync baseline scenario when graph data changes
  useEffect(() => {
    // Early returns to prevent unnecessary calls
    if (!currentSimulationId || !graph.dataUpdatedAt || !canMutateScenarios) {
      return;
    }
    
    // Skip if we've already synced this dataUpdatedAt value
    if (lastSyncedDataUpdatedAtRef.current === graph.dataUpdatedAt) {
      return;
    }
    
    // Skip if sync is already in progress
    if (baselineSyncInProgressRef.current) {
      return;
    }
    
    // Check if baseline exists and if it actually needs syncing
    const baseline = getBaselineScenario();
    
    // If baseline exists and is already in sync, skip
    if (baseline && baseline.graphDataUpdatedAt === graph.dataUpdatedAt) {
      lastSyncedDataUpdatedAtRef.current = graph.dataUpdatedAt;
      return;
    }
    
    // If baseline doesn't exist, syncBaselineScenario will create it
    // If baseline exists but is out of sync, syncBaselineScenario will update it
    
    // Perform sync with guard to prevent concurrent calls
    baselineSyncInProgressRef.current = true;
    syncBaselineScenario(graph)
      .then(() => {
        lastSyncedDataUpdatedAtRef.current = graph.dataUpdatedAt;
        
        // After syncing, re-run the baseline scenario with the updated graph data
        const updatedBaseline = getBaselineScenario();
        if (updatedBaseline && graph.nodes.length > 0) {
          // Run the baseline scenario to update its results
          runScenario(updatedBaseline.id, graph, runConfig, run).catch((error) => {
            errorService.logError('Error running baseline scenario after sync', error, {
              action: 'runBaselineScenario',
              component: 'SimulationWorkspace',
            });
          });
        }
      })
      .catch((error) => {
        errorService.logError('Error syncing baseline scenario', error, {
          action: 'syncBaselineScenario',
          component: 'SimulationWorkspace',
        });
      })
      .finally(() => {
        baselineSyncInProgressRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.dataUpdatedAt, currentSimulationId, canMutateScenarios, syncBaselineScenario, getBaselineScenario]);
  
  // Update URL when workspace context changes (but not when loading from URL)
  useEffect(() => {
    const urlSimulation = simulationIdParam;
    const urlVersion = versionIdParam;

    if (urlSimulation && urlSimulation !== currentSimulationId) {
      return;
    }

    if (urlVersion && urlVersion !== currentVersionId) {
      return;
    }
    
    if (currentSimulationId && currentVersionId) {
      if (urlSimulation === currentSimulationId && urlVersion === currentVersionId) {
        return;
      }

      const newParams = new URLSearchParams();
      newParams.set('simulation', currentSimulationId);
      newParams.set('version', currentVersionId);
      applyWorkspaceViewParam(newParams, activeView);
      setSearchParams(newParams, { replace: true });
      return;
    }

    if (currentSimulationId && !currentVersionId) {
      if (urlSimulation === currentSimulationId && !urlVersion) {
        return;
      }

      const newParams = new URLSearchParams();
      newParams.set('simulation', currentSimulationId);
      applyWorkspaceViewParam(newParams, activeView);
      setSearchParams(newParams, { replace: true });
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSimulationId, currentVersionId]); // Only depend on context, not searchParams

  // Manual run handler
  const handleRunSimulation = useCallback(async () => {
    if (isRunning) return; // Don't run if already running
    
    const validationIssues = collectSimulationRunErrors(graph);
    if (validationIssues.length > 0) {
      setRunErrors(validationIssues);
      setResult(undefined);
      return;
    }

    clearRunErrors();
    setIsRunning(true);
    try {
      const result = await run(graph, runConfig);
      setResult(result);
    } catch (err) {
      errorService.logError('Simulation execution failed', err, {
        action: 'runSimulation',
        component: 'SimulationWorkspace'
      });
      setResult(undefined);
      const issue = err instanceof Error ? err.message : String(err);
      const nodeId = (err as any).nodeId || '';
      const nodeName = (err as any).nodeName || '';
      setRunErrors([{ nodeId, nodeName, message: issue }]);
    } finally {
      setIsRunning(false);
    }
  }, [clearRunErrors, graph, isRunning, run, runConfig, setIsRunning, setResult, setRunErrors]);

  const autoRunKey = useMemo(() => {
    const seed = runConfig.seed ?? 'none';
    return `${graph.id}:${graph.dataUpdatedAt}:${runConfig.trials}:${seed}`;
  }, [graph.id, graph.dataUpdatedAt, runConfig.trials, runConfig.seed]);

  useEffect(() => {
    if (graph.nodes.length === 0) {
      return;
    }

    if (isRunning || lastAutoRunKeyRef.current === autoRunKey) {
      return;
    }

    if (autoRunDebounceRef.current) {
      clearTimeout(autoRunDebounceRef.current);
      autoRunDebounceRef.current = null;
    }

    autoRunDebounceRef.current = setTimeout(() => {
      lastAutoRunKeyRef.current = autoRunKey;
      void handleRunSimulation();
    }, 200);

    return () => {
      if (autoRunDebounceRef.current) {
        clearTimeout(autoRunDebounceRef.current);
        autoRunDebounceRef.current = null;
      }
    };
  }, [autoRunKey, graph.nodes.length, handleRunSimulation, isRunning]);

  const handleRestoreVersion = useCallback(async (version: SavedVersion) => {
    setIsGraphLoading(true);

    try {
      const clonedGraph = JSON.parse(JSON.stringify(version.graph)) as SimulationGraph;
      const graphForWorkspace = await applyAutomaticGraphLayout(clonedGraph);

      loadGraph(graphForWorkspace);
      clearHistory();

      const simulation = getSimulation(version.simulation_id);

      // Preserve current access level when restoring version
      setWorkspaceContext(
        version.simulation_id,
        simulation?.name ?? currentSimulationName,
        version.id,
        version.name,
        currentAccessLevel
      );

      const newParams = new URLSearchParams();
      newParams.set('simulation', version.simulation_id);
      newParams.set('version', version.id);
      applyWorkspaceViewParam(newParams, activeView);
      setSearchParams(newParams, { replace: true });

      setLastAutoSaveAtStore(version.created_at);
      setAutoSaveStatusStore('idle');
      setIsHistoryOpen(false);
    } finally {
      setIsGraphLoading(false);
    }
  }, [
    clearHistory,
    currentSimulationName,
    activeView,
    getSimulation,
    loadGraph,
    setAutoSaveStatusStore,
    setLastAutoSaveAtStore,
    setSearchParams,
    setWorkspaceContext,
    currentAccessLevel
  ]);

  const isNarrowWorkspace = workspaceViewportWidth < WORKSPACE_NARROW_WIDTH;

  return (
    <ReactFlowProvider>
      <div
        className={`workspace workspace-no-inspector ${isNarrowWorkspace ? 'workspace-narrow' : ''}`}
      >
        <section className="canvas canvas-full" aria-busy={(isRunning || isGraphLoading) && isGraphView}>
          <div className="canvas-content">
            {isGraphView ? (
              <SimulationCanvas
                setReactFlowInstance={setReactFlowInstance}
                onOpenHistory={() => setIsHistoryOpen(true)}
                historyDisabled={!currentSimulationId}
                onOpenNodeOptions={setNodeOptionsNodeId}
                isReadOnly={isExampleSimulation || isViewer}
                isGraphLoading={isGraphLoading}
              />
            ) : isInputsAssumptionsView ? (
              <InputsAssumptionsTable
                isReadOnly={isExampleSimulation || isViewer}
                onOpenNodeOptions={setNodeOptionsNodeId}
              />
            ) : isModelSheetView ? (
              <ModelSheetView
                onOpenNodeOptions={setNodeOptionsNodeId}
              />
            ) : isScenariosView ? (
              <ScenariosPanel />
            ) : activeView === 'results' ? (
              <ResultsView
                reactFlowInstance={reactFlowInstance}
                onHoverMetric={setHoveredOutputNodeId}
              />
            ) : (
              <ResultsView
                reactFlowInstance={reactFlowInstance}
                onHoverMetric={setHoveredOutputNodeId}
              />
            )}
          </div>
        </section>
      </div>
      
      {!isExampleSimulation && (
        <VersionHistoryPanel
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          onRestore={handleRestoreVersion}
        />
      )}

      <NodeOptionsPanel
        nodeId={nodeOptionsNodeId}
        isOpen={Boolean(nodeOptionsNodeId)}
        onClose={() => setNodeOptionsNodeId(null)}
      />

      <AssistantPanel />

    </ReactFlowProvider>
  );
};
